import { createContext } from "react"
import { CurrentUser, PostOwner, PostStatus, PostTargetDate, SystemSettings, Tenant, TenantStatus } from "@fider/models"

export class FiderSession {
  private pPage: string
  private pContextID: string
  private pTenant: Tenant
  private pUser: CurrentUser | undefined
  private pProps: { [key: string]: any } = {}
  private pPostStatuses: PostStatus[]
  private pPostOwners: PostOwner[]
  private pPostTargetDates: PostTargetDate[]

  constructor(data: any) {
    this.pPage = data.page
    this.pContextID = data.contextID
    this.pProps = data.props
    this.pUser = data.user
    this.pTenant = data.tenant
    this.pPostStatuses = data.postStatuses || []
    this.pPostOwners = data.postOwners || []
    this.pPostTargetDates = data.postTargetDates || []
  }

  public get page(): string {
    return this.pPage
  }

  public get contextID(): string {
    return this.pContextID
  }

  public get user(): CurrentUser {
    if (!this.pUser) throw new Error("User is undefined")
    return this.pUser
  }

  public get tenant(): Tenant {
    return this.pTenant
  }

  public get postStatuses(): PostStatus[] {
    return this.pPostStatuses
  }

  // Non-throwing lookup: unlike a fixed enum, a status slug can legitimately
  // not exist as a row (reserved "duplicate"/"deleted", or a status deleted
  // after this post was set to it) — callers must degrade gracefully.
  public getPostStatus(slug: string): PostStatus | undefined {
    return this.pPostStatuses.find((s) => s.slug === slug)
  }

  public get postOwners(): PostOwner[] {
    return this.pPostOwners
  }

  public getPostOwner(slug: string): PostOwner | undefined {
    return this.pPostOwners.find((o) => o.slug === slug)
  }

  public get postTargetDates(): PostTargetDate[] {
    return this.pPostTargetDates
  }

  public getPostTargetDate(slug: string): PostTargetDate | undefined {
    return this.pPostTargetDates.find((t) => t.slug === slug)
  }

  public get props(): { [key: string]: any } {
    return this.pProps
  }

  public get isAuthenticated(): boolean {
    return !!this.pUser
  }

  public get isModerationRequiredForNewPost(): boolean {
    return this.pTenant.isPro && this.pTenant.isModerationEnabled && this.isAuthenticated && this.pUser!.role === "visitor" && !this.pUser!.isTrusted
  }

  public get showModerationControls(): boolean {
    return this.pTenant.isPro && this.pTenant.isModerationEnabled && this.isAuthenticated && (this.pUser?.isCollaborator ?? false)
  }
}

export class FiderImpl {
  private pSettings!: SystemSettings
  private pSession!: FiderSession

  public initialize = (initData?: any): FiderImpl => {
    if (initData) {
      this.pSettings = initData.settings
      this.pSession = new FiderSession(initData)
      return this
    }

    const el = document.getElementById("server-data")
    const data = el ? JSON.parse(el.textContent || el.innerText) : {}
    this.pSettings = data.settings
    this.pSession = new FiderSession(data)
    return this
  }

  public get currentLocale(): string {
    if (this.session.tenant) {
      return this.session.tenant.locale
    }
    return this.settings.locale
  }

  public get session(): FiderSession {
    return this.pSession
  }

  public get settings(): SystemSettings {
    return this.pSettings
  }

  public get isReadOnly(): boolean {
    return this.session.tenant && this.session.tenant.status === TenantStatus.Locked
  }

  public isProduction(): boolean {
    return this.pSettings.environment === "production"
  }

  public isSingleHostMode(): boolean {
    return this.pSettings.mode === "single"
  }
}

export const Fider = new FiderImpl()

export const FiderContext = createContext<FiderImpl>(Fider)
